import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Typography from '@components/common/typography';
import { Icon } from '@components/icons';

import {
    apiSandboxService,
    SandboxApiError,
    SandboxSessionRequest,
    SandboxSessionResponse
} from '@api';
import { centeredPopupFeatures } from '@utils/connect_helpers';

import xepptGoldLogo from '@svg/header_logo.svg';

import IntegratePanel from './IntegratePanel';
import styles from './store_checkout.module.scss';

type Phase = 'idle' | 'loading' | 'minted' | 'success' | 'cancelled' | 'error';

interface Product {
    id: string;
    name: string;
    blurb: string;
    price: number;
    emoji: string;
    tone: string; // maps to a gradient class in the stylesheet
}

interface SuccessPayload {
    transaction_id: string;
    amount: number;
    customer_id?: string;
    username?: string;
}

interface IncomingPostMessage {
    source?: unknown;
    type?: unknown;
    transaction_id?: unknown;
    amount?: unknown;
    customer_id?: unknown;
    username?: unknown;
}

// Demo catalogue — sandbox-only mock storefront. No real inventory or money.
const PRODUCTS: Product[] = [
    { id: 'sku_headphones', name: 'Aurora Headphones', blurb: 'Wireless · noise-cancelling', price: 129, emoji: '🎧', tone: 'tone_a' },
    { id: 'sku_sneakers', name: 'Trailblaze Sneakers', blurb: 'All-terrain · unisex', price: 89, emoji: '👟', tone: 'tone_b' },
    { id: 'sku_watch', name: 'Pulse Smartwatch', blurb: 'Fitness · AMOLED', price: 199, emoji: '⌚', tone: 'tone_c' },
    { id: 'sku_coffee', name: 'Single-Origin Roast', blurb: 'Whole bean · 1 kg', price: 24, emoji: '☕', tone: 'tone_d' },
    { id: 'sku_backpack', name: 'Summit Backpack', blurb: '28 L · water-resistant', price: 74, emoji: '🎒', tone: 'tone_e' },
    { id: 'sku_lamp', name: 'Halo Desk Lamp', blurb: 'Dimmable · USB-C', price: 45, emoji: '💡', tone: 'tone_f' }
];

const PRODUCTS_BY_ID: Record<string, Product> = PRODUCTS.reduce(
    (acc, p) => {
        acc[p.id] = p;
        return acc;
    },
    {} as Record<string, Product>
);

// Flat demo shipping rate — sandbox-only, applied whenever the cart isn't empty.
// TODO(founder confirm): $9.99 CAD is a placeholder flat rate; swap for the
// real demo-store figure once confirmed.
const SHIPPING_FLAT_CENTS = 999;

// Flat demo XEPPT fee — sandbox-only, applied whenever the cart isn't empty.
// TODO(founder confirm): $2.50 CAD is a placeholder flat fee; swap for the
// real demo-store figure once confirmed.
const FEE_FLAT_CENTS = 250;

const STORE_STEPS = ['Create a session', 'Customer pays in the popup', 'Handle payment.succeeded'];

const STORE_SNIPPET = `// Load the XEPPT Buttons SDK on your page, then render the button:

xeppt.Buttons({
  // 1 · Create a checkout session on your server -> returns a connect_token
  createSession: () =>
    fetch('/api/create-session', { method: 'POST' })
      .then((r) => r.json())
      .then((s) => s.connect_token),

  // 2 · The customer pays in the popup; you receive the transaction id
  onApprove: ({ transaction_id }) => {
    window.location.href = '/success?tx=' + transaction_id;
  },
}).render('#pay-with-xeppt');`;

// This portal has no build-time "development environment" signal to branch on
// (no REACT_APP_ENV/hostname check exists anywhere in the app — the dev-portal
// env pill only reflects the merchant's test/live API key mode, which is an
// unrelated concept). So instead of inventing one, this probes the dev-demo
// (LIVE) mint first and falls back to the sandbox (test) mint on a 404 — the
// exact signal the backend uses to mean "not a development environment". That
// lights up the real-money demo automatically on dev today and is a no-op
// safety net once the dev-only endpoint stops existing outside dev.
async function mintCheckoutSession(
    body: SandboxSessionRequest
): Promise<SandboxSessionResponse> {
    try {
        return await apiSandboxService.mintDevDemoSession(body);
    } catch (err) {
        if (err instanceof SandboxApiError && err.status === 404) {
            return apiSandboxService.mintSession(body);
        }
        throw err;
    }
}

const StoreCheckout = () => {
    const [cart, setCart] = useState<Record<string, number>>({});
    const [phase, setPhase] = useState<Phase>('idle');
    const [connectToken, setConnectToken] = useState<string | null>(null);
    const [orderId, setOrderId] = useState<string | null>(null);
    const [successPayload, setSuccessPayload] = useState<SuccessPayload | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const cartLines = useMemo(
        () =>
            Object.entries(cart)
                .filter(([, qty]) => qty > 0)
                .map(([id, qty]) => ({ product: PRODUCTS_BY_ID[id], qty }))
                .filter((line): line is { product: Product; qty: number } => !!line.product),
        [cart]
    );
    const itemCount = cartLines.reduce((sum, l) => sum + l.qty, 0);
    // `total` is the product subtotal (pre-shipping) — kept as-is since it also
    // drives the line-item breakdown sent to the backend.
    const total = cartLines.reduce((sum, l) => sum + l.product.price * l.qty, 0);
    // Flat demo shipping — waived on an empty cart.
    const shippingAmount = itemCount > 0 ? SHIPPING_FLAT_CENTS / 100 : 0;
    // Flat demo XEPPT fee — same waived-on-empty-cart convention as shipping.
    const feeAmount = itemCount > 0 ? FEE_FLAT_CENTS / 100 : 0;
    // grandTotal is the actual amount charged (subtotal + shipping + fee) —
    // this is what gets sent as `amount` to the mint endpoint and shown on the button.
    const grandTotal = total + shippingAmount + feeAmount;

    // Mirror phase into a ref so the stable []-dep message listener reads the live phase.
    const phaseRef = useRef(phase);
    useEffect(() => {
        phaseRef.current = phase;
    }, [phase]);

    const handleMessage = useCallback((event: MessageEvent) => {
        if (phaseRef.current !== 'minted') return;
        if (event.origin !== window.location.origin) return;
        const data = event.data as IncomingPostMessage;
        if (data?.source !== 'xeppt-connect') return;
        // A CHARGE success carries transaction_id (an identity success carries `code`),
        // so a co-mounted sign-in popup can never spoof this payment panel.
        if (data.type === 'success' && typeof data.transaction_id === 'string') {
            setSuccessPayload({
                transaction_id: data.transaction_id,
                amount: typeof data.amount === 'number' ? data.amount : grandTotal,
                customer_id: typeof data.customer_id === 'string' ? data.customer_id : undefined,
                username: typeof data.username === 'string' ? data.username : undefined
            });
            setPhase('success');
        } else if (data.type === 'cancel') {
            setPhase('cancelled');
        }
    }, [grandTotal]);

    useEffect(() => {
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [handleMessage]);

    const addToCart = (id: string) =>
        setCart((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }));

    const decrement = (id: string) =>
        setCart((prev) => {
            const next = { ...prev };
            const q = (next[id] ?? 0) - 1;
            if (q <= 0) delete next[id];
            else next[id] = q;
            return next;
        });

    const clearCart = () => setCart({});

    const handleReset = () => {
        setPhase('idle');
        setConnectToken(null);
        setOrderId(null);
        setSuccessPayload(null);
        setErrorMessage(null);
    };

    const handlePay = async () => {
        if (grandTotal <= 0) return;

        // Open the popup synchronously inside the click gesture — before any await —
        // or the browser treats it as a background open and blocks it.
        const win = window.open('', 'xeppt_sandbox', centeredPopupFeatures());
        if (!win) {
            setErrorMessage('Popup was blocked. Please allow popups for this site and try again.');
            setPhase('error');
            return;
        }

        setPhase('loading');
        setConnectToken(null);
        setOrderId(null);
        setSuccessPayload(null);
        setErrorMessage(null);

        try {
            const lineItems = cartLines.map((l) => ({
                name: l.product.name,
                description: l.product.blurb,
                quantity: l.qty,
                unit_price_cents: Math.round(l.product.price * 100)
            }));

            const data = await mintCheckoutSession({
                // Decimal string — the backend rejects a numeric amount with 400.
                // amount is the actual charge total, so it must equal
                // subtotal_cents + shipping_cents + fee_cents below (the backend
                // treats those as display-only and does not sum them itself).
                amount: grandTotal.toFixed(2),
                currency: 'CAD',
                description: `${itemCount} item${itemCount === 1 ? '' : 's'} · XEPPT Demo Store`,
                line_items: lineItems,
                subtotal_cents: Math.round(total * 100),
                shipping_cents: Math.round(shippingAmount * 100),
                fee_cents: Math.round(feeAmount * 100)
            });

            setConnectToken(data.connect_token);
            setOrderId(data.order_id);
            setPhase('minted');

            const safePopupPath =
                typeof data.popup_path === 'string' &&
                data.popup_path.startsWith('/') &&
                !data.popup_path.startsWith('//')
                    ? data.popup_path
                    : null;
            if (!win.closed && safePopupPath) {
                // Route to the sandbox popup so the developer's portal session is never
                // disturbed — /connect-sandbox uses in-memory auth only.
                const sandboxPath = safePopupPath.replace(/^\/connect\b/, '/connect-sandbox');
                win.location.href = `${window.location.origin}${sandboxPath}`;
            } else if (!win.closed) {
                win.close();
                setErrorMessage('Unexpected sandbox response — could not open the checkout popup.');
                setPhase('error');
            }
        } catch (err) {
            if (!win.closed) win.close();
            const apiErr = err instanceof SandboxApiError ? err : null;
            const code = apiErr?.code;
            const msg = apiErr?.message ?? 'Unexpected error — could not mint sandbox session.';
            setErrorMessage(code ? `[${code}] ${msg}` : msg);
            setPhase('error');
        }
    };

    const isLoading = phase === 'loading';
    const showResult = phase !== 'idle' && phase !== 'loading';

    return (
        <div className={styles.section}>
            <div className={styles.section_header}>
                <Typography variant="h5">Pay with XEPPT</Typography>
                <Typography variant="body3" className={styles.section_hint}>
                    A live demo storefront — add items to the cart and check out with the real Pay
                    with XEPPT popup. On dev this runs a real charge, so the customer and merchant
                    wallet balances actually move.
                </Typography>
            </div>

            <div className={styles.store_card}>
                <div className={styles.store_top}>
                    <div className={styles.store_brand}>
                        <span className={styles.store_logo}>🛍️</span>
                        <div>
                            <p className={styles.store_name}>XEPPT Demo Store</p>
                            <p className={styles.store_tag}>Sandbox · test mode</p>
                        </div>
                    </div>
                    <div className={styles.cart_badge}>
                        <Icon name="outlined_wallet" width={16} height={16} />
                        {itemCount} {itemCount === 1 ? 'item' : 'items'}
                    </div>
                </div>

                <div className={styles.store_body}>
                    <div className={styles.products}>
                        {PRODUCTS.map((p) => {
                            const qty = cart[p.id] ?? 0;
                            return (
                                <div key={p.id} className={styles.product}>
                                    <div className={[styles.thumb, styles[p.tone]].join(' ')}>
                                        <span className={styles.thumb_emoji}>{p.emoji}</span>
                                    </div>
                                    <div className={styles.product_info}>
                                        <p className={styles.product_name}>{p.name}</p>
                                        <p className={styles.product_blurb}>{p.blurb}</p>
                                    </div>
                                    <div className={styles.product_foot}>
                                        <span className={styles.product_price}>
                                            ${p.price.toFixed(2)}
                                        </span>
                                        {qty === 0 ? (
                                            <button
                                                className={styles.add_btn}
                                                onClick={() => addToCart(p.id)}
                                                type="button">
                                                <Icon name="plus" width={14} height={14} />
                                                Add
                                            </button>
                                        ) : (
                                            <div className={styles.stepper}>
                                                <button
                                                    className={styles.step_btn}
                                                    onClick={() => decrement(p.id)}
                                                    type="button"
                                                    aria-label="Remove one">
                                                    <Icon name="minus" width={14} height={14} />
                                                </button>
                                                <span className={styles.step_qty}>{qty}</span>
                                                <button
                                                    className={styles.step_btn}
                                                    onClick={() => addToCart(p.id)}
                                                    type="button"
                                                    aria-label="Add one">
                                                    <Icon name="plus" width={14} height={14} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <aside className={styles.cart}>
                        <div className={styles.cart_head}>
                            <Typography variant="body2" weight="semibold">
                                Your cart
                            </Typography>
                            {itemCount > 0 && (
                                <button
                                    className={styles.clear_btn}
                                    onClick={clearCart}
                                    type="button">
                                    Clear
                                </button>
                            )}
                        </div>

                        {cartLines.length === 0 ? (
                            <div className={styles.cart_empty}>
                                <Icon name="outlined_wallet" width={26} height={26} />
                                <p>Your cart is empty. Add a few items to try checkout.</p>
                            </div>
                        ) : (
                            <>
                                <div className={styles.cart_lines}>
                                    {cartLines.map((l) => (
                                        <div key={l.product.id} className={styles.cart_line}>
                                            <span className={styles.cart_line_emoji}>
                                                {l.product.emoji}
                                            </span>
                                            <div className={styles.cart_line_info}>
                                                <p className={styles.cart_line_name}>
                                                    {l.product.name}
                                                </p>
                                                <p className={styles.cart_line_meta}>
                                                    {l.qty} × ${l.product.price.toFixed(2)}
                                                </p>
                                            </div>
                                            <span className={styles.cart_line_total}>
                                                ${(l.product.price * l.qty).toFixed(2)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className={styles.cart_summary}>
                                    <div className={styles.cart_summary_row}>
                                        <span>Subtotal</span>
                                        <span className={styles.cart_summary_val}>
                                            ${total.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className={styles.cart_summary_row}>
                                        <span>Shipping</span>
                                        <span className={styles.cart_summary_val}>
                                            ${shippingAmount.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className={styles.cart_summary_row}>
                                        <span>Fees</span>
                                        <span className={styles.cart_summary_val}>
                                            ${feeAmount.toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.cart_total_row}>
                                    <span>Total</span>
                                    <span className={styles.cart_total_val}>
                                        ${grandTotal.toFixed(2)} CAD
                                    </span>
                                </div>
                            </>
                        )}

                        <button
                            type="button"
                            className={styles.pay_btn}
                            aria-label="Pay with XEPPT"
                            disabled={grandTotal <= 0 || isLoading}
                            onClick={() => void handlePay()}>
                            <span className={styles.pay_btn_text}>Pay with</span>
                            {isLoading ? (
                                <Icon
                                    name="loading_spinner"
                                    width={16}
                                    height={16}
                                    className={styles.pay_btn_spinner}
                                />
                            ) : (
                                <img src={xepptGoldLogo} alt="" className={styles.pay_btn_logo} />
                            )}
                        </button>
                        <p className={styles.pay_note}>
                            <Icon name="lock" width={12} height={12} />
                            Secure checkout — on dev this runs a real Pay-with-XEPPT charge.
                        </p>
                    </aside>
                </div>
            </div>

            {showResult && (
                <div className={styles.result_card}>
                    <div className={styles.result_header}>
                        <Typography variant="body4" weight="semibold">
                            Checkout result
                        </Typography>
                        <button className={styles.reset_btn} onClick={handleReset} type="button">
                            <Icon name="reload" width={14} height={14} />
                            Reset
                        </button>
                    </div>

                    {connectToken !== null && (
                        <div className={styles.token_section}>
                            <div className={styles.token_row}>
                                <p className={styles.token_label}>connect_token</p>
                                <p className={styles.token_value}>{connectToken}</p>
                            </div>
                            {orderId !== null && (
                                <div className={styles.token_row}>
                                    <p className={styles.token_label}>order_id</p>
                                    <p className={styles.token_value}>{orderId}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {phase === 'minted' && (
                        <div className={styles.status_panel}>
                            <Icon
                                name="loading_spinner"
                                width={20}
                                height={20}
                                className={styles.spinner}
                            />
                            <Typography variant="body4">
                                Popup opened — waiting for the customer to pay…
                            </Typography>
                        </div>
                    )}

                    {phase === 'success' && successPayload && (
                        <div className={styles.status_panel}>
                            <Icon
                                name="filled_check"
                                width={24}
                                height={24}
                                className={styles.icon_success}
                            />
                            <div className={styles.status_text}>
                                <Typography variant="body4" weight="semibold">
                                    Payment succeeded
                                </Typography>
                                <div className={styles.token_row}>
                                    <p className={styles.token_label}>transaction_id</p>
                                    <p className={styles.token_value}>
                                        {successPayload.transaction_id}
                                    </p>
                                </div>
                                <div className={styles.token_row}>
                                    <p className={styles.token_label}>amount</p>
                                    <p className={styles.token_value}>
                                        ${successPayload.amount.toFixed(2)} CAD
                                    </p>
                                </div>
                                {successPayload.customer_id && (
                                    <div className={styles.token_row}>
                                        <p className={styles.token_label}>customer_id</p>
                                        <p className={styles.token_value}>
                                            {successPayload.customer_id}
                                        </p>
                                    </div>
                                )}
                                {successPayload.username && (
                                    <div className={styles.token_row}>
                                        <p className={styles.token_label}>username</p>
                                        <p className={styles.token_value}>
                                            @{successPayload.username}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {phase === 'cancelled' && (
                        <div className={styles.status_panel}>
                            <Icon
                                name="close_outlined"
                                width={24}
                                height={24}
                                className={styles.icon_cancelled}
                            />
                            <Typography variant="body4">
                                Checkout was cancelled by the customer.
                            </Typography>
                        </div>
                    )}

                    {phase === 'error' && (
                        <div className={styles.status_panel}>
                            <Icon
                                name="error_outlined"
                                width={24}
                                height={24}
                                className={styles.icon_error}
                            />
                            <Typography variant="body4">
                                {errorMessage ?? 'Failed to mint sandbox session.'}
                            </Typography>
                        </div>
                    )}
                </div>
            )}

            <IntegratePanel
                steps={STORE_STEPS}
                code={STORE_SNIPPET}
                docHref="https://docs.xeppt.com"
            />
        </div>
    );
};

export default StoreCheckout;
